import { TaskSuggestion, TaskSuggestionGroup } from "./definitions";

export const createTaskFromData = (
  task: any,
  taskIndex: number,
  groupIndex: number,
  defaultOwner: string,
  idPrefix: string,
): TaskSuggestion => ({
  id: `${idPrefix}-task-${groupIndex + 1}-${taskIndex + 1}`,
  name: task.name || task.taskName || `Task ${taskIndex + 1}`,
  type: task.type || task.inquiryType || "",
  status: task.status || "Not started",
  dueDate: task.dueDate || task.dueDateString || "",
  owners: task.owner || defaultOwner,
  actualStart: task.actualStart || task.actualStartDateString || "",
  completionDate: task.completionDate || task.actualCompletionDateString || "",
  plannedStart: task.plannedStart || task.plannedStartDateString || "",
  complexity: task.complexity || "",
  priority: task.priority || "",
  milestone: Boolean(task.milestone),
  order: task.order !== undefined ? task.order : taskIndex,
});

export const createTaskGroupFromData = (
  taskGroup: any,
  index: number,
  defaultOwner: string,
  idPrefix: string,
): TaskSuggestionGroup => {
  const tasks = taskGroup.tasks || [];

  return {
    id: `${idPrefix}-tg-${index + 1}`,
    name: taskGroup.name || `Task Group ${index + 1}`,
    description:
      taskGroup.description || taskGroup.name || `Task Group ${index + 1}`,
    status: taskGroup.status || "pending",
    order: taskGroup.order !== undefined ? taskGroup.order : index,
    tasks: Array.isArray(tasks)
      ? tasks.map((task: any, taskIndex: number) =>
          createTaskFromData(task, taskIndex, index, defaultOwner, idPrefix),
        )
      : [],
  };
};

// Helper function to parse template data consistently
export const parseTemplateData = (templateData: any): any[] => {
  let apiResponse: any = templateData;

  if (templateData.template) {
    if (typeof templateData.template === "string") {
      try {
        apiResponse = JSON.parse(templateData.template);
      } catch (_parseError) {
        apiResponse = [];
      }
    } else if (Array.isArray(templateData.template)) {
      apiResponse = templateData.template;
    } else {
      apiResponse = [];
    }
  } else if (Array.isArray(templateData)) {
    apiResponse = templateData;
  }

  return Array.isArray(apiResponse) ? apiResponse : [];
};

export const extractScanTaskData = (
  scanTaskResult: any,
  defaultOwner: string,
  orderOffset = 0,
): TaskSuggestionGroup | null => {
  if (!scanTaskResult?.tasks) {
    return null;
  }
  const { tasks, dueDate, taskGroup } = scanTaskResult;
  let parsedTasks: any[] = [];

  if (typeof tasks === "string") {
    try {
      const jsonParsed = JSON.parse(tasks);
      if (Array.isArray(jsonParsed)) {
        parsedTasks = jsonParsed;
      }
    } catch {}
    if (parsedTasks.length === 0) {
      const cleanedString = tasks
        .replace(/^'?\[?'?/, "")
        .replace(/'?\]?'?$/, "");
      const taskStrings = cleanedString.split(/\},\s*\{/);
      parsedTasks = taskStrings
        .map((taskStr) => {
          let fullTaskStr = taskStr;
          if (!fullTaskStr.startsWith("{")) fullTaskStr = `{${fullTaskStr}`;
          if (!fullTaskStr.endsWith("}")) fullTaskStr = `${fullTaskStr}}`;
          const task: any = {};
          const taskItemMatch = fullTaskStr.match(
            /'task_item'\s*:\s*'(.*?)'\s*(?:,|$)/,
          );
          const taskDescMatch = fullTaskStr.match(
            /'task_description'\s*:\s*'((?:[^']|'(?!\s*[,}]))+)'[\s,}]/,
          );
          const verbatimMatch = fullTaskStr.match(
            /'verbatim'\s*:\s*'(.*?)'\s*(?:\}|$)/,
          );
          if (taskItemMatch) {
            task.task_item = taskItemMatch[1].replace(/<%>/g, "\n");
          }
          if (taskDescMatch) {
            task.task_description = taskDescMatch[1].replace(/<%>/g, "\n");
          }
          if (verbatimMatch) {
            let verbatimText = verbatimMatch[1];
            verbatimText = verbatimText.replace(
              /([:.?!])-?n([a-zA-Z]\. )/g,
              "$1\n $2",
            );
            task.verbatim = verbatimText;
          }

          return task;
        })
        .filter((task) => task.task_item);
    }
  } else if (Array.isArray(tasks)) {
    parsedTasks = tasks;
  }
  if (!Array.isArray(parsedTasks) || parsedTasks.length === 0) {
    return null;
  }

  const transformedTasks: TaskSuggestion[] = parsedTasks.map(
    (task: any, index: number) => {
      let taskDescription = "";
      if (task.task_description) {
        taskDescription = task.task_description;
      }

      if (task.verbatim) {
        const verbatimSection = `**Verbatim:** ${task.verbatim}`;
        taskDescription = taskDescription
          ? `${taskDescription}\n\n${verbatimSection}`
          : verbatimSection;
      }
      if (!taskDescription) {
        taskDescription = "";
      }
      return {
        id: `scan-task-${index + 1}`,
        name: (task.task_item || `Task ${index + 1}`).replace(/<%>/g, "\n"),
        description: taskDescription,
        type: task.type || task.inquiryType || "",
        status: "Not started",
        dueDate: dueDate || "",
        owners: defaultOwner,
        actualStart: "",
        completionDate: "",
        plannedStart: "",
        complexity: "",
        priority: "",
        milestone: false,
        order: task.order !== undefined ? task.order : index,
      };
    },
  );

  const taskGroupData: TaskSuggestionGroup = {
    id: "scan-tg-1",
    name: taskGroup || "",
    description: taskGroup || "",
    status: "Not started",
    type: "",
    dueDate: dueDate || "",
    owners: defaultOwner,
    order: orderOffset,
    tasks: transformedTasks,
  };

  return taskGroupData;
};

export const hasScanTaskData = (scanTaskResult: any): boolean => {
  return Boolean(scanTaskResult?.tasks && scanTaskResult.taskGroup);
};

export const sortTaskGroupsByOrder = (
  taskGroups: TaskSuggestionGroup[],
): TaskSuggestionGroup[] => {
  return [...taskGroups].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
};

export const sortTasksByOrder = (tasks: TaskSuggestion[]): TaskSuggestion[] => {
  return [...tasks].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
};

export const getNextTaskGroupOrder = (
  taskGroups: TaskSuggestionGroup[],
): number => {
  if (taskGroups.length === 0) return 0;
  const maxOrder = Math.max(...taskGroups.map((tg) => tg.order ?? 0));
  return maxOrder + 1;
};

export const getNextTaskOrder = (tasks: TaskSuggestion[]): number => {
  if (tasks.length === 0) return 0;
  const maxOrder = Math.max(...tasks.map((t) => t.order ?? 0));
  return maxOrder + 1;
};
